import { Clock, FileText, HelpCircle, LogOut, Shield } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import Logo from '@/assets/images/logo/full_logo.svg';
import { Button } from '@/components/ui/button';
import { CONTACT_URL, PRIVACY_POLICY_URL, ROUTES, TERMS_AND_CONDITIONS_URL } from '@/constants';
import { useCurrentAccessTokenSelector } from '@/store/slices/auth/session.slice';
import type { NavigationItem, User } from '@/types/layout.types';
import { cn } from '@/utils';

interface LegacyWebSidebarProps {
	bottomNav: NavigationItem[];
	navigation: NavigationItem[];
	currentTimezone: string;
	currentPageName: string;
	user: User | null | undefined;
	signOut: () => void | Promise<void>;
	onAccountClick: () => void;
	onHowToPlayClick: () => void;
	isLoadingUser: boolean;
	setSidebarOpen: (isOpen: boolean) => void;
	isRegisteredUser: boolean;
}

export function LegacyWebSidebar({
	bottomNav,
	navigation,
	currentTimezone,
	currentPageName,
	user,
	signOut,
	onAccountClick,
	onHowToPlayClick,
	isLoadingUser,
	setSidebarOpen,
	isRegisteredUser,
}: LegacyWebSidebarProps) {
	const accessToken = useCurrentAccessTokenSelector();
	const { pathname } = useLocation();

	return (
		<aside
			className={cn(
				'fixed left-0 top-0 z-50 h-full w-64 bg-transparent',
				currentPageName === 'LobbyDetails' && 'border-r border-white/10'
			)}
		>
			<div className="flex h-full flex-col">
				<div className="my-5 flex w-full justify-center">
					<Link to="/" prefetch="viewport">
						<img src={Logo} alt="App Logo" className="h-full w-[156px]" />
					</Link>
				</div>

				<ul className="flex flex-col items-start justify-start gap-y-4 pl-4">
					{bottomNav.map((item) => {
						const isActive =
							currentPageName === item.name ||
							(item.name === 'home' && currentPageName === 'Auth') ||
							(item.name === 'courses' && pathname.startsWith(ROUTES.COURSES));

						return (
							<li key={item.name} className="w-full">
								<Link
									to={item.href ?? '/'}
									prefetch="viewport"
									className={cn(
										'flex items-center space-x-2 rounded px-3 py-2',
										isActive
											? 'rounded-none border-r-4 border-blue-custom-2 text-[#F7F7F8]'
											: 'text-[#777777] hover:text-white'
									)}
								>
									<item.icon className="size-5" />
									<span className="text-[20px] font-semibold">{item.tabName}</span>
								</Link>
							</li>
						);
					})}
				</ul>

				<div className="mb-3 ml-4 mt-5 border-t border-[#777777]" />

				<nav className="flex-1 p-4">
					<ul className="space-y-1">
						{navigation.map((item) => {
							const isActive = currentPageName === item.name;

							if (item.name === 'account') {
								if (!isRegisteredUser) return null;

								return (
									<li key={item.name}>
										<button
											type="button"
											className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-[#777777] transition-colors hover:bg-white/10 hover:text-white"
											onClick={onAccountClick}
										>
											<item.icon className="size-5" />
											{item.tabName}
										</button>
									</li>
								);
							}

							if (item.name === 'HowToPlay') {
								return (
									<li key={item.name}>
										<button
											type="button"
											className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-[#777777] transition-colors hover:bg-white/10 hover:text-white"
											onClick={onHowToPlayClick}
										>
											<item.icon className="size-5" />
											{item.tabName}
										</button>
									</li>
								);
							}

							if (!item.href) return null;

							return (
								<li key={item.name}>
									<Link
										to={item.href}
										prefetch="viewport"
										className={cn(
											'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
											isActive
												? 'bg-blue-500/20 text-blue-400'
												: 'text-[#777777] hover:bg-white/10 hover:text-white'
										)}
										onClick={() => setSidebarOpen(false)}
									>
										<item.icon className="size-5" />
										{item.tabName}
									</Link>
								</li>
							);
						})}
					</ul>
				</nav>

				<div className="p-4 text-[#D4D5D8]">
					<div className="mt-3 space-y-1 text-[#777777]">
						<a
							href={TERMS_AND_CONDITIONS_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors hover:bg-white/5 hover:text-white"
						>
							<FileText className="size-4" />
							Terms of Service
						</a>
						<a
							href={PRIVACY_POLICY_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors hover:bg-white/5 hover:text-white"
						>
							<Shield className="size-4" />
							Privacy Policy
						</a>
						<a
							href={CONTACT_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors hover:bg-white/5 hover:text-white"
						>
							<HelpCircle className="size-4" />
							Support
						</a>
					</div>

					<div className="flex items-center gap-2 px-2 py-3 text-sm text-[#777777]">
						<Clock className="size-4" />
						<span>Timezone: {currentTimezone}</span>
					</div>

					{!isLoadingUser && isRegisteredUser && (user || accessToken) && (
						<Button
							variant="ghost"
							className="mt-3 w-full justify-start px-2 hover:bg-white/10"
							onClick={() => void signOut()}
						>
							<LogOut className="mr-1 size-5" />
							<div className="font-['DM_Sans'] text-base font-bold leading-tight text-[#F7F7F8]">
								Sign Out
							</div>
						</Button>
					)}
				</div>
			</div>
		</aside>
	);
}
